// Bottom inspector (PRD §6.1): per-probe lines for the selected monitor,
// its per-probe state and its recent effective state changes.
import { Link } from "@tanstack/react-router";
import { ExternalLink, VolumeX, X } from "lucide-react";

import { useListIncidents, useListMonitorStatusChanges } from "@/api/generated";
import { TelemetryPanel } from "@/components/monitors/telemetry";
import { StatusBadge, StatusDot } from "@/components/status/StatusBadge";
import { Button } from "@/components/ui/button";
import { ago, duration } from "@/lib/format";
import { monitorPlace, monitorTarget, probeLabel, useIsInternal, useLinkMap, useMonitorMap, useNodeMap, useProbeMap, useStateMap } from "@/lib/lookups";
import { useNow } from "@/lib/now";
import { useUI } from "@/store/ui";

export function Inspector() {
  const open = useUI((s) => s.inspectorOpen);
  const id = useUI((s) => s.inspectorMonitorId);
  const close = useUI((s) => s.closeInspector);
  if (!open || !id) return null;
  return (
    <section className="max-h-[55vh] shrink-0 overflow-y-auto border-t bg-card/60 backdrop-blur" aria-label="Inspector">
      <InspectorBody monitorId={id} onClose={close} />
    </section>
  );
}

function InspectorBody({ monitorId, onClose }: { monitorId: string; onClose: () => void }) {
  const monitors = useMonitorMap();
  const states = useStateMap();
  const probes = useProbeMap();
  const links = useLinkMap();
  const nodes = useNodeMap();
  const internal = useIsInternal();
  const incidents = useListIncidents(undefined, { query: { enabled: internal } });
  const changes = useListMonitorStatusChanges(monitorId, { effective_only: true }, { query: { refetchInterval: 30_000 } });
  const now = useNow();
  const m = monitors.get(monitorId);
  const st = states.get(monitorId);
  if (!m) {
    return (
      <div className="flex items-center justify-between px-4 py-3 text-sm text-muted-foreground">
        Monitor not found (it may have been deleted).
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close inspector"><X /></Button>
      </div>
    );
  }
  const openIncident = (incidents.data ?? []).find((i) => i.monitor_id === m.id);
  return (
    <div className="grid gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {st && <StatusBadge status={st.status} />}
            <h2 className="truncate text-sm font-semibold">{m.name}</h2>
            {st?.agreement && <span className="tabular text-xs text-muted-foreground">{st.agreement}</span>}
            {st?.flapping && <span className="text-xs text-status-degraded">Flapping</span>}
            {st?.muted && <span className="flex items-center gap-1 text-xs text-muted-foreground"><VolumeX className="size-3" /> Muted</span>}
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {m.check_type} {monitorTarget(m)} {monitorPlace(m, links, nodes) && `· ${monitorPlace(m, links, nodes)}`}
            {st?.reason && ` · ${st.reason}`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {openIncident && (
            <Button asChild size="sm" variant="outline">
              <Link to="/incidents/$incidentId" params={{ incidentId: openIncident.id }}>Incident</Link>
            </Button>
          )}
          <Button asChild size="sm" variant="ghost">
            <Link to="/monitors/$monitorId" params={{ monitorId: m.id }}><ExternalLink /> Monitor</Link>
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close inspector"><X /></Button>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <TelemetryPanel key={m.id} monitorId={m.id} probes={probes} height={170} down={st?.status === "DOWN"} />
        <div className="grid content-start gap-3 text-sm">
          <div>
            <h3 className="mb-1 text-xs font-medium text-muted-foreground uppercase">Per probe</h3>
            {st && st.probes.length > 0 ? (
              <ul className="grid gap-1">
                {st.probes.map((p) => (
                  <li key={p.probe_id} className="flex items-center gap-2">
                    <StatusDot status={p.status} />
                    <span className="truncate">{probeLabel(probes, p.probe_id, st.probes.map((x) => x.probe_id))}</span>
                    <span className="ml-auto shrink-0 truncate text-xs text-muted-foreground">{p.reason || STATUS_TEXT[p.status]} · {duration(p.since, null, now)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">No probe is assigned or reporting.</p>
            )}
          </div>
          <div>
            <h3 className="mb-1 text-xs font-medium text-muted-foreground uppercase">Recent changes</h3>
            <ul className="grid gap-1">
              {(changes.data ?? []).slice(0, 8).map((c, i) => (
                <li key={c.at + i} className="flex items-center gap-2 text-xs">
                  <StatusDot status={c.status} />
                  <span className="truncate">{c.reason || STATUS_TEXT[c.status]}</span>
                  <span className="ml-auto shrink-0 text-muted-foreground" title={c.at}>{ago(c.at, now)}</span>
                </li>
              ))}
              {changes.data?.length === 0 && <li className="text-xs text-muted-foreground">No changes recorded yet.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = {
  UP: "Up", DEGRADED: "Degraded", CRITICAL: "Critical", DOWN: "Down", MAINTENANCE: "Maintenance", UNKNOWN: "Unknown",
};
